import type { ApiClient } from '@carbonwebmail/api-client'
import type { PreSendCheckDto } from '@carbonwebmail/contracts'
import { Link, useParams } from 'react-router'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { count } from '../../ui/format.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { SchedulePanel } from './SchedulePanel.js'
import { useSendReview } from './useSendReview.js'

/**
 * The last screen before a campaign leaves.
 *
 * Sending is the only irreversible thing in this product, and it used to be one
 * press on a list — beside a row, next to a delete button, with nothing between
 * the thought and several thousand emails. This is what goes between.
 *
 * **Only the blocking checks stop it.** An unverified domain is the single
 * biggest cause of a campaign landing in spam and it is *not* blocking, because
 * our check establishes presence rather than correctness: refusing on it would
 * lock somebody out of their own product on the strength of a DNS lookup that
 * might simply have failed. It is said bluntly and it stays their decision.
 *
 * **The audience is counted now, not when the segment was saved.** That is what
 * a segment is, and the number here is produced by the same predicate the send
 * itself uses — so it is what the campaign actually reaches, not an estimate of
 * it.
 *
 * **A test goes through the real queue.** It is the same path, the same rate
 * budget and the same worker, which is what makes it a test of what recipients
 * get rather than of a second, faster route that nothing else uses.
 */
export function SendReviewPage({ client }: { readonly client: ApiClient }) {
  const { campaignId = '' } = useParams()
  const review = useSendReview({ client, campaignId })
  const { state, notice, problem, busy } = review

  return (
    <>
      <header className="mb-6">
        <Link className="font-medium text-[13px] text-t3 hover:text-t1" to="/campaigns">
          ← Campaigns
        </Link>
        <h1 className="mt-2 font-bold text-[22px] text-t1 -tracking-[0.01em]">Before you send</h1>
        <p className="mt-1 text-[13px] text-t2">
          A campaign goes out once and cannot be recalled. Everything below is true right now.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone={notice.tone}>{notice.text}</Alert>}

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Checking…</p>}
        onRetry={() => void review.retry()}
      >
        {(readiness) => (
          <div className="max-w-2xl">
            <section className="mb-6 rounded-card border border-line bg-surface p-5">
              <h2 className="font-bold text-[15px] text-t1">{readiness.name}</h2>
              <p className="mt-1 text-[13px] text-t2">{readiness.subject}</p>
              <p className="mt-4 font-bold text-[28px] text-t1 -tracking-[0.01em]">
                {count(readiness.audience)}
              </p>
              <p className="text-[13px] text-t2">
                {readiness.audience === 1 ? 'person' : 'people'}, counted now
              </p>
            </section>

            <ul className="mb-6 flex flex-col gap-3">
              {readiness.checks.map((check) => (
                <Check key={check.id} check={check} />
              ))}
            </ul>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={busy || !readiness.canSend}
                onClick={() => void review.send()}
                className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[14px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
              >
                {busy ? 'Working…' : `Send to ${count(readiness.audience)}`}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void review.sendTest()}
                className="rounded-pill border-2 border-brand px-5 py-2.5 font-bold text-[14px] text-brand disabled:opacity-60"
              >
                Send one to me first
              </button>

              {/*
                Scheduling sits beside sending rather than on a screen of its
                own, because it is the same decision answered differently —
                this campaign, to these people, now or at a stated moment. A
                separate screen would mean reading the checklist twice.

                Offered only once the checklist allows a send: a campaign that
                cannot go out now cannot go out on Tuesday either, and letting
                somebody schedule past a blocking check would move the failure
                to a moment when nobody is watching.
              */}
              {readiness.canSend ? (
                <SchedulePanel
                  busy={busy}
                  /* The server's answer first: it survives a reload, where the
                     screen's own copy of what was just set does not. */
                  scheduled={review.scheduled ?? readiness.schedule}
                  outcome={review.outcome}
                  now={new Date()}
                  onSchedule={(wanted) => void review.schedule(wanted)}
                  onCancel={() => void review.unschedule()}
                />
              ) : null}
            </div>

            {readiness.canSend ? null : (
              <p className="mt-3 text-[13px] text-danger">
                Something above has to change before this can be sent.
              </p>
            )}
          </div>
        )}
      </Async>
    </>
  )
}

/**
 * `attention` is its own tone and never the danger one.
 *
 * The same rule the send log and the sending-domain screen follow: red is for
 * "this cannot happen", and something that is merely worth reading before you
 * press send is not that. Colouring both the same teaches people that red means
 * nothing in particular.
 */
const TONES = {
  ready: { tone: 'ok', label: 'ready' },
  attention: { tone: 'warn', label: 'worth reading' },
  blocking: { tone: 'danger', label: 'stops the send' },
} as const

function Check({ check }: { readonly check: PreSendCheckDto }) {
  const display = TONES[check.outcome]

  return (
    <li className="rounded-card border border-line bg-surface p-4">
      <span className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[14px] text-t1">{check.title}</span>
        <StatusPill tone={display.tone}>{display.label}</StatusPill>
      </span>
      <p className="mt-1 text-[13px] text-t2 leading-relaxed">{check.detail}</p>
    </li>
  )
}
